import { message } from "antd";
import { v4 as uuidv4 } from "uuid";
import { stock } from "../../../src/components/PointOfSale/Core/stock.js";
import messages from "../../errors/messages.en-IN.json";
import useAuthStore from "../../stores/useAuthStore";
import { useEventBus } from "../../lib/components/EventBusProvider";
import { needsPopup } from "./productPopupUtils";

/**
 * Quantity manipulation (increase, decrease, change, delete) for ProductsPopup items.
 *
 * @param {Object} params
 * @param {Array}    params.selectedItems
 * @param {Function} params.setSelectedItems
 * @param {Object}   params.cart
 * @param {Function} params.setCart
 * @param {Function} params.setProductStock
 * @param {string}   params.unit             - Current weight unit ("kg" / "g")
 */
const useProductQuantity = ({
  selectedItems,
  setSelectedItems,
  cart,
  setCart,
  setProductStock,
  unit,
}) => {
  const tillData = useAuthStore.getState().tillData;
  const { dispatchWeight } = useEventBus();

  const openWeightPopupForItem = (product, callback) => {
    const existing = selectedItems.find((i) => i.mProductId === product.mProductId && i.uom === (product.uom || product.csUomId));
    dispatchWeight(
      {
        ...product,
        weight: existing?.weight || product.weight || 0,
        rawWeight: existing?.rawWeight || product.rawWeight || product.weight?.toString() || "",
      },
      () => {},
      null,
      () => {},
      (updatedProduct) => {
        if (!updatedProduct) return;
        callback(updatedProduct);
      }
    );
  };

  const matchesItem = (item, product) =>
    item.mProductId === product.mProductId && item.uom === (product.uom || product.csUomId);

  const handleIncrease = async (product) => {
    const isInCart = selectedItems.find((item) => matchesItem(item, product));
    const prevSelectedItems = [...selectedItems];
    const updatedSelectedItems = isInCart
      ? selectedItems.map((item) =>
          matchesItem(item, product)
            ? {
                ...item,
                weight: Number(item.weight) + 1,
                rawWeight: (Number(item.weight) + 1).toString(),
                unit: unit || product?.uomData?.[0]?.name?.toLowerCase() || "kg",
              }
            : item
        )
      : [
          ...selectedItems,
          {
            ...product,
            weight: 1,
            rawWeight: "1",
            uom: product.uom || product.csUomId,
            unit: unit || product?.uomData?.[0]?.name?.toLowerCase() || "kg",
            lineId: uuidv4().replace(/-/g, "").toUpperCase(),
          },
        ];
    const tempCart = { ...cart, items: updatedSelectedItems };
    const isPreOrderPage = window.location.pathname.includes("/preorder");
    if (navigator.onLine && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y" && !isPreOrderPage) {
      const stockCheckResult = await stock({
        cart: tempCart,
        setCart,
        tillData,
        setProductStock,
        isFinalValidation: false,
      });
      if (!stockCheckResult) {
        setSelectedItems(prevSelectedItems);
        return;
      }
    }
    setSelectedItems(updatedSelectedItems);
  };

  const handleDecrease = (product) => {
    const isInCart = selectedItems.find((item) => matchesItem(item, product));
    if (isInCart && isInCart.weight > 1) {
      setSelectedItems(
        selectedItems.map((item) =>
          matchesItem(item, product)
            ? {
                ...item,
                weight: Number(item.weight) - 1,
                rawWeight: (Number(item.weight) - 1).toString(),
                unit: item.unit || unit || product?.uomData?.[0]?.name?.toLowerCase() || "kg",
              }
            : item
        )
      );
    } else if (isInCart && isInCart.weight <= 1) {
      setSelectedItems(selectedItems.filter((item) => !matchesItem(item, product)));
    }
  };

  const handleQuantityChange = async (product, value) => {
    const isDecimal = product?.uomData?.[0]?.decimal === "Y";
    let newValue = value;

    if (newValue === "") {
      newValue = "0";
    }

    const currentWeight = product.rawWeight || (product.weight !== undefined ? product.weight.toString() : "0");
    const isInitialZero = currentWeight === "0";
    if (isInitialZero && /\d/.test(newValue) && !newValue.startsWith("0.")) {
      newValue = newValue.replace(/^0+/, "");
      if (newValue === "") newValue = "0";
    }

    const isValidInput = isDecimal ? /^(\d*\.?\d{0,3}|\d*\.|\d*\.\d{0,3}\.)$/.test(newValue) : /^\d*$/.test(newValue);
    if (!isValidInput) return;

    const parsedValue = newValue === "0" || newValue === "." || newValue === "0." || newValue === "0.0" || newValue === "0.00" ? 0 : parseFloat(newValue) || 0;

    if (parsedValue < 0) {
      message.error(`${messages.info.quantity_not_negative}`);
      return;
    }

    const isInCart = selectedItems.find((item) => matchesItem(item, product));
    const prevSelectedItems = [...selectedItems];
    let updatedSelectedItems = [];

    if (isInCart) {
      if (parsedValue === 0 && ![".", "0.", "0.0", "0.00"].includes(newValue)) {
        updatedSelectedItems = selectedItems.filter((item) => !matchesItem(item, product));
      } else {
        updatedSelectedItems = selectedItems.map((item) =>
          matchesItem(item, product)
            ? {
                ...item,
                weight: isDecimal ? parsedValue : Math.floor(parsedValue),
                rawWeight: newValue,
                unit: item.unit || unit || product?.uomData?.[0]?.name?.toLowerCase() || "kg",
              }
            : item
        );
      }
    } else if (parsedValue > 0 || [".", "0.", "0.0", "0.00"].includes(newValue)) {
      updatedSelectedItems = [
        ...selectedItems,
        {
          ...product,
          weight: isDecimal ? parsedValue : Math.floor(parsedValue),
          rawWeight: newValue,
          uom: product.uom || product.csUomId,
          unit: unit || product?.uomData?.[0]?.name?.toLowerCase() || "kg",
          lineId: uuidv4().replace(/-/g, "").toUpperCase(),
        },
      ];
    } else {
      return;
    }

    const tempCart = { ...cart, items: updatedSelectedItems };
    const isPreOrderPage = window.location.pathname.includes("/preorder");
    if (navigator.onLine && tillData?.tillAccess?.csBunit?.allowNegativeStock !== "Y" && !isPreOrderPage) {
      const stockCheckResult = await stock({
        cart: tempCart,
        setCart,
        tillData,
        setProductStock,
        isFinalValidation: false,
      });
      if (!stockCheckResult) {
        setSelectedItems(prevSelectedItems);
        return;
      }
    }
    setSelectedItems(updatedSelectedItems);
  };

  const handleDelete = (itemToDelete) => {
    setSelectedItems(selectedItems.filter((item) => item.lineId !== itemToDelete.lineId));
  };

  return {
    handleIncrease,
    handleDecrease,
    handleQuantityChange,
    handleDelete,
    openWeightPopupForItem,
    needsPopup,
  };
};

export default useProductQuantity;
